import { memo } from 'react'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Flexbox } from '@/components/ui/flexbox'
import {
	Expand01Icon,
	ThumbsDownIcon,
	ThumbsUpIcon
} from '@/components/ui/icons'
import {
	OpsVettingGalleryItemsContainer,
	OpsVettingImageTitle
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { ServiceVettingCriteriaFlags } from '@/features/service-vetting/components/service-vetting-criteria-flags'
import { useAddServiceGalleryImageFlags } from '@/features/service-vetting/query/use-add-service-gallery-image-flags.ts'
import { useRemoveServiceGalleryImageFlags } from '@/features/service-vetting/query/use-remove-service-gallery-image-flags.ts'
import { useUpdateIsApprovedGalleryImg } from '@/features/service-vetting/query/use-update-is-approved-gallery-img.ts'
import type { ServiceGalleryImage } from '@/features/service-vetting/types.ts'
import { useTranslation } from '@/translations/i18n'

export const ServiceVettingCriteriaRejectedImageItem = memo(
	({
		image,
		galleryId,
		hideAction,
		onClickExpand
	}: {
		image: ServiceGalleryImage
		galleryId: number
		hideAction?: boolean
		onClickExpand?: (image: ServiceGalleryImage) => void
	}) => {
		const { t } = useTranslation()

		const updateIsApprovedGalleryImg = useUpdateIsApprovedGalleryImg({
			serviceId: image.submission_id,
			imgId: image.id,
			galleryId
		})

		const addServiceGalleryImageFlags = useAddServiceGalleryImageFlags({
			serviceId: image.submission_id,
			sectionId: galleryId
		})
		const removeServiceGalleryImageFlags =
			useRemoveServiceGalleryImageFlags({
				serviceId: image.submission_id,
				sectionId: galleryId
			})

		return (
			<OpsVettingGalleryItemsContainer direction='column' gap={4}>
				<GalleryImageItem
					bottomRightSlot={
						!hideAction && (
							<ButtonLegacy
								className='show-on-hover'
								onClick={() => onClickExpand?.(image)}
								shape='square'
								size='sm'
								type='button'
								variant='secondary'
							>
								<Expand01Icon />
							</ButtonLegacy>
						)
					}
					src={image.thumbnails?.xs || image.url}
				/>
				<Flexbox direction='column' fullWidth gap={2}>
					<OpsVettingImageTitle>{image.name}</OpsVettingImageTitle>
					<Flexbox fullWidth gap={3}>
						<ButtonLegacy
							className='w-full'
							disabled={hideAction}
							onClick={() =>
								image.is_approved !== true &&
								updateIsApprovedGalleryImg.mutate({
									is_approved: true
								})
							}
							size={'sm'}
							type='button'
							variant={
								image.is_approved === null || !image.is_approved
									? 'secondary'
									: 'primarySuccess'
							}
						>
							<ThumbsUpIcon />
							{t('vetting:criteria.service.gallery.accept')}
						</ButtonLegacy>
						<ButtonLegacy
							className='w-full'
							disabled={hideAction}
							onClick={() =>
								image.is_approved !== false &&
								updateIsApprovedGalleryImg.mutate({
									is_approved: false
								})
							}
							size={'sm'}
							type='button'
							variant={
								image.is_approved === null || image.is_approved
									? 'secondary'
									: 'primaryDanger'
							}
						>
							<ThumbsDownIcon />
							{t('vetting:criteria.service.gallery.reject')}
						</ButtonLegacy>
					</Flexbox>
					{image.is_approved === false && (
						<ServiceVettingCriteriaFlags
							addFlag={(flag) =>
								addServiceGalleryImageFlags.mutate({
									flags: [flag],
									itemId: image.id
								})
							}
							flags={image.flags}
							hideAction={hideAction}
							removeFlag={(flag) =>
								removeServiceGalleryImageFlags.mutate({
									flags: [flag],
									itemId: image.id
								})
							}
							types={['gallery_image']}
						/>
					)}
				</Flexbox>
			</OpsVettingGalleryItemsContainer>
		)
	}
)
